import React, { useState } from 'react';
import {
  Grid,
  Typography,
  Button,
  IconButton,
  Link,
  Card,
  CardContent,
  Box,
  Divider,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  TextField,
} from '@mui/material';
import { Edit, Launch, Business, Group, Schedule } from '@mui/icons-material';
import { useApiGet, useApiSend } from '../../../utils/httpClientTs';
import {
  getSPDChapterInfo,
  updateSPDChapterInfo,
  getSPDChapterTypes,
  getSPDRegions,
  getSPDSponsors,
} from '../../../endpoints/chaptersTs';
import { roles } from '../../../utils/variables';
import { canViewTransferRequests } from '../../../utils/permissions';
import { SPDChapterInfoResponse } from '../../../types/chapters';
import UserSearchModal from '../../../Components/UserSearchModal';
import AlertItem from '../../../Components/AlertItem';

interface SPDInfoProps {
  id: string;
  spdAccess: boolean;
}

interface ChapterType {
  id: string;
  name: string;
}

interface Region {
  id: string;
  name: string;
}

interface Sponsor {
  id: string;
  name: string;
}

interface SPDInfoUpdateRequest {
  name?: string;
  chapter_type_id?: string;
  region_id?: string;
  sponsor_id?: string | null;
  international_representative_id?: string | null;
  this_years_meeting_date?: string | null;
}

const SPDInfo: React.FC<SPDInfoProps> = ({ id, spdAccess }) => {
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingField, setEditingField] = useState<string | null>(null);
  const [formData, setFormData] = useState<SPDInfoUpdateRequest>({});
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [userSearchModalOpen, setUserSearchModalOpen] = useState(false);
  const [selectedUserName, setSelectedUserName] = useState<string>('');

  const {
    data: spdInfo,
    isFetching,
    refetch,
  } = useApiGet<SPDChapterInfoResponse, { id: string }>(
    ['spd-info', id],
    getSPDChapterInfo,
    {
      enabled: true,
      retry: 1,
    },
    { id: id }
  );

  const { data: chapterTypes } = useApiGet<ChapterType[]>(
    ['chapter-types'],
    getSPDChapterTypes,
    {
      enabled: true,
      retry: 1,
    }
  );

  const { data: regions } = useApiGet<Region[]>(['regions'], getSPDRegions, {
    enabled: true,
    retry: 1,
  });

  const { data: sponsors } = useApiGet<Sponsor[]>(
    ['sponsors'],
    getSPDSponsors,
    {
      enabled: true,
      retry: 1,
    }
  );

  const updateInfoMutation = useApiSend(
    (data: SPDInfoUpdateRequest) => updateSPDChapterInfo(id, data),
    () => {
      setEditDialogOpen(false);
      setEditingField(null);
      setFormData({});
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);
      refetch();
    },
    (error) => {
      setError(error?.message || 'Error updating SPD info');
    }
  );

  const canEdit = (): boolean => {
    if (spdAccess) {
      const userRole = localStorage.getItem('role');
      const rolesAllowed = [roles.Admin, roles.Support];
      return rolesAllowed.includes(userRole || '');
    }
    return false;
  };

  const canLinkTransferRequests = (): boolean => {
    if (spdAccess) {
      return canViewTransferRequests();
    }
    return false;
  };

  const formatNumber = (num: number): string => {
    return num.toLocaleString();
  };

  const handleEditField = (field: string) => {
    setEditingField(field);
    if (field === 'this_years_meeting_date') {
      setFormData({
        this_years_meeting_date: spdInfo?.this_years_meeting_date ?? null,
      });
    } else {
      setFormData({});
    }
    setEditDialogOpen(true);
  };

  const handleCloseEditDialog = () => {
    setEditDialogOpen(false);
    setEditingField(null);
    setFormData({});
    setError(null);
    setSelectedUserName('');
  };

  const handleSubmitEdit = () => {
    if (
      editingField &&
      formData[editingField as keyof SPDInfoUpdateRequest] !== undefined
    ) {
      updateInfoMutation.mutate(formData);
    }
  };

  const getFieldValue = (field: string): string => {
    if (!spdInfo) return '';

    switch (field) {
      case 'name':
        return spdInfo.name;
      case 'chapter_type_id':
        return spdInfo.chapter_type_id;
      case 'region_id':
        return spdInfo.region_id;
      case 'sponsor_id':
        return spdInfo.sponsor_id || '';
      case 'international_representative_id':
        return spdInfo.international_representative_id || '';
      case 'this_years_meeting_date':
        return spdInfo.this_years_meeting_date ?? '';
      default:
        return '';
    }
  };

  const getFieldLabel = (field: string): string => {
    switch (field) {
      case 'name':
        return 'Chapter Name';
      case 'chapter_type_id':
        return 'Chapter Type';
      case 'region_id':
        return 'Membership Region';
      case 'sponsor_id':
        return 'EB Liaison';
      case 'international_representative_id':
        return 'International Chapter Representative';
      case 'this_years_meeting_date':
        return "This Year's Meeting Date";
      default:
        return '';
    }
  };

  const getFieldOptions = (
    field: string
  ): Array<{ id: string; name: string }> => {
    switch (field) {
      case 'chapter_type_id':
        return chapterTypes || [];
      case 'region_id':
        return regions || [];
      case 'sponsor_id':
        return sponsors || [];
      default:
        return [];
    }
  };

  const handleUserSelect = (user: any) => {
    setFormData({
      ...formData,
      international_representative_id: user.id,
    });
    setSelectedUserName(user.full_name);
    setUserSearchModalOpen(false);
  };

  if (isFetching) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <Typography variant="h6" color="text.secondary">
          Loading SPD information...
        </Typography>
      </Box>
    );
  }

  if (!spdInfo) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <Typography variant="h6" color="text.secondary">
          No SPD information found
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {showSuccessMessage && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setShowSuccessMessage(false)}
        >
          SPD information updated successfully!
        </Alert>
      )}
      {error && (
        <Box sx={{ mb: 2 }}>
          <AlertItem
            severity="error"
            description={error}
            onClose={() => setError(null)}
            keepOpen={true}
          />
        </Box>
      )}

      <Grid container spacing={3}>
        {/* Header Card */}
        <Grid item xs={12}>
          <Card elevation={2}>
            <CardContent>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="flex-start"
                mb={2}
              >
                <Box>
                  <Typography variant="h4" component="h1" gutterBottom>
                    {spdInfo.name}
                  </Typography>
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    {spdInfo.abbreviation}
                  </Typography>
                  {spdInfo.website && (
                    <Link
                      href={spdInfo.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        textDecoration: 'none',
                        color: 'primary.main',
                        '&:hover': {
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      <Launch sx={{ fontSize: 16, mr: 0.5 }} />
                      Visit Website
                    </Link>
                  )}
                </Box>
                {canEdit() && (
                  <IconButton
                    onClick={() => handleEditField('name')}
                    sx={{ color: '#1976d2' }}
                  >
                    <Edit />
                  </IconButton>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Basic Info Cards */}
        <Grid item xs={12} md={6}>
          <Card elevation={1}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <Business sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Chapter Details</Typography>
              </Box>
              <Stack spacing={2}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="body2" color="text.secondary">
                    Chapter Type:
                  </Typography>
                  <Box display="flex" alignItems="center">
                    <Typography variant="body2" sx={{ mr: 1 }}>
                      {spdInfo.chapter_type}
                    </Typography>
                    {canEdit() && (
                      <IconButton
                        size="small"
                        onClick={() => handleEditField('chapter_type_id')}
                        sx={{ color: '#1976d2' }}
                      >
                        <Edit />
                      </IconButton>
                    )}
                  </Box>
                </Box>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="body2" color="text.secondary">
                    Membership Region:
                  </Typography>
                  <Box display="flex" alignItems="center">
                    <Typography variant="body2" sx={{ mr: 1 }}>
                      {spdInfo.region_name}
                    </Typography>
                    {canEdit() && (
                      <IconButton
                        size="small"
                        onClick={() => handleEditField('region_id')}
                        sx={{ color: '#1976d2' }}
                      >
                        <Edit />
                      </IconButton>
                    )}
                  </Box>
                </Box>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="body2" color="text.secondary">
                    This Year's Meeting Date:
                  </Typography>
                  <Box display="flex" alignItems="center">
                    <Typography variant="body2" sx={{ mr: 1 }}>
                      {spdInfo.this_years_meeting_date || 'Not set'}
                    </Typography>
                    {canEdit() && (
                      <IconButton
                        size="small"
                        onClick={() =>
                          handleEditField('this_years_meeting_date')
                        }
                        sx={{ color: '#1976d2' }}
                      >
                        <Edit />
                      </IconButton>
                    )}
                  </Box>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card elevation={1}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <Group sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Leadership</Typography>
              </Box>
              <Stack spacing={2}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="body2" color="text.secondary">
                    EB Liaison:
                  </Typography>
                  <Box display="flex" alignItems="center">
                    <Typography variant="body2" sx={{ mr: 1 }}>
                      {spdInfo.sponsor || 'Not assigned'}
                    </Typography>
                    {canEdit() && (
                      <IconButton
                        size="small"
                        onClick={() => handleEditField('sponsor_id')}
                        sx={{ color: '#1976d2' }}
                      >
                        <Edit />
                      </IconButton>
                    )}
                  </Box>
                </Box>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="body2" color="text.secondary">
                    International Representative:
                  </Typography>
                  <Box display="flex" alignItems="center">
                    <Typography variant="body2" sx={{ mr: 1 }}>
                      {spdInfo.international_representative || 'Not assigned'}
                    </Typography>
                    {canEdit() && (
                      <IconButton
                        size="small"
                        onClick={() =>
                          handleEditField('international_representative_id')
                        }
                        sx={{ color: '#1976d2' }}
                      >
                        <Edit />
                      </IconButton>
                    )}
                  </Box>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Statistics Card */}
        <Grid item xs={12}>
          <Card elevation={1}>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <Schedule sx={{ mr: 1, color: 'primary.main' }} />
                <Typography variant="h6">Statistics</Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={3}>
                <Grid item xs={12} md={4}>
                  <Box textAlign="center">
                    <Typography variant="h4" color="primary.main">
                      {formatNumber(spdInfo.active_members)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Active Members
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box textAlign="center">
                    <Typography variant="h4" color="primary.main">
                      {formatNumber(spdInfo.active_chapters)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Active Chapters
                    </Typography>
                    <Link
                      target="_blank"
                      rel="noopener noreferrer"
                      href={`/local-chapters?spd=${id}&status=active`}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mt: 1,
                      }}
                    >
                      <Launch sx={{ fontSize: 14, mr: 0.5 }} />
                      View Chapters
                    </Link>
                  </Box>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Box textAlign="center">
                    <Typography variant="h4" color="primary.main">
                      {formatNumber(spdInfo.transfer_requests)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Transfer Requests
                    </Typography>
                    {canLinkTransferRequests() && (
                      <Link
                        target="_blank"
                        rel="noopener noreferrer"
                        href={`/seeking-transfer?spd_id=${id}&status_id=60baf6c89e6f4343900e4dba8188548d`}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mt: 1,
                        }}
                      >
                        <Launch sx={{ fontSize: 14, mr: 0.5 }} />
                        View Transfer Requests
                      </Link>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={handleCloseEditDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit {getFieldLabel(editingField || '')}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            {editingField === 'name' ? (
              <TextField
                fullWidth
                label={getFieldLabel(editingField)}
                value={formData.name || getFieldValue(editingField)}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                variant="outlined"
              />
            ) : editingField === 'international_representative_id' ? (
              <Box>
                <TextField
                  fullWidth
                  label="Selected User"
                  value={
                    selectedUserName ||
                    spdInfo?.international_representative ||
                    'Not assigned'
                  }
                  variant="outlined"
                  InputProps={{
                    readOnly: true,
                  }}
                />
                <Button
                  variant="outlined"
                  onClick={() => setUserSearchModalOpen(true)}
                  sx={{ mt: 2 }}
                  fullWidth
                >
                  Search for User
                </Button>
              </Box>
            ) : editingField === 'this_years_meeting_date' ? (
              <TextField
                fullWidth
                type="date"
                label={getFieldLabel('this_years_meeting_date')}
                value={
                  formData.this_years_meeting_date ??
                  getFieldValue('this_years_meeting_date') ??
                  ''
                }
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    this_years_meeting_date: e.target.value || null,
                  })
                }
                variant="outlined"
                InputLabelProps={{ shrink: true }}
              />
            ) : (
              <FormControl fullWidth>
                <InputLabel>{getFieldLabel(editingField || '')}</InputLabel>
                <Select
                  value={
                    formData[editingField as keyof SPDInfoUpdateRequest] ||
                    getFieldValue(editingField || '')
                  }
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      [editingField || '']: e.target.value,
                    })
                  }
                  label={getFieldLabel(editingField || '')}
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {getFieldOptions(editingField || '').map((option) => (
                    <MenuItem key={option.id} value={option.id}>
                      {option.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>

          {updateInfoMutation.isError && (
            <Box sx={{ mt: 2 }}>
              <AlertItem
                severity="error"
                description="An error occurred while updating. Please try again."
                onClose={() => {}}
                keepOpen={true}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEditDialog}>Cancel</Button>
          <Button
            onClick={handleSubmitEdit}
            variant="contained"
            disabled={updateInfoMutation.isPending}
          >
            {updateInfoMutation.isPending ? 'Updating...' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* User Search Modal */}
      <UserSearchModal
        modalOpen={userSearchModalOpen}
        setModalOpen={setUserSearchModalOpen}
        setUser={handleUserSelect}
        chapter={null}
        spd={null}
        activeMembers={false}
        userObjectRet={true}
      />
    </Box>
  );
};

export default SPDInfo;
